import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import { formatDate } from '../../utils/dateUtils';
import StagePipeline from '../components/StagePipeline';

const PRIORITY_COLORS = { urgent: '#ef4444', high: '#f59e0b', normal: '#3b82f6', low: '#6b7280' };
const VIEW_MODES = { list: 'List', kanban: 'Pipeline' };

export default function WorkflowItemListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');

  // Filters
  const [workflowId, setWorkflowId] = useState(searchParams.get('workflow') || '');
  const [status, setStatus] = useState('active');
  const [priority, setPriority] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, templatesRes] = await Promise.all([
        api.getWorkflowItems({
          workflow_id: workflowId || undefined,
          status,
          priority: priority || undefined,
          overdue_only: overdueOnly || undefined,
          limit: 200,
        }),
        api.getWorkflowTemplates(),
      ]);
      setItems(itemsRes.items || []);
      setTotal(itemsRes.total || 0);
      setTemplates(templatesRes || []);

      // Load stages for selected workflow (for kanban view)
      if (workflowId) {
        const stagesRes = await api.getWorkflowStages(workflowId);
        setStages(stagesRes || []);
      } else {
        setStages([]);
      }
    } catch (err) {
      console.error('Failed to load items:', err);
    } finally {
      setLoading(false);
    }
  }, [workflowId, status, priority, overdueOnly]);

  useEffect(() => { loadData(); }, [loadData]);

  const clearFilters = () => {
    setWorkflowId('');
    setStatus('active');
    setPriority('');
    setOverdueOnly(false);
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200 }}>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>All Items</h1>
          <p style={{ color: '#6b7280', fontSize: 14, margin: '4px 0 0' }}>{total} total items</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {Object.entries(VIEW_MODES).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setView(key)}
              className="btn btn-sm"
              style={{
                backgroundColor: view === key ? '#D4A843' : '#f3f4f6',
                color: view === key ? '#1a1a2e' : '#6b7280',
                fontWeight: view === key ? 700 : 500,
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="filter-row" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <select className="sales-input" value={workflowId} onChange={e => setWorkflowId(e.target.value)} style={{ width: 180 }}>
          <option value="">All Workflows</option>
          {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select className="sales-input" value={status} onChange={e => setStatus(e.target.value)} style={{ width: 120 }}>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="">All</option>
        </select>
        <select className="sales-input" value={priority} onChange={e => setPriority(e.target.value)} style={{ width: 120 }}>
          <option value="">All Priority</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="normal">Normal</option>
          <option value="low">Low</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#6b7280', cursor: 'pointer' }}>
          <input type="checkbox" checked={overdueOnly} onChange={e => setOverdueOnly(e.target.checked)} />
          Overdue only
        </label>
        <button className="btn btn-sm" onClick={clearFilters} style={{ background: '#f3f4f6', color: '#6b7280' }}>
          Clear
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>Loading...</div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>No items found</div>
      ) : view === 'kanban' && stages.length > 0 ? (
        <KanbanView items={items} stages={stages} navigate={navigate} />
      ) : (
        <ListView items={items} navigate={navigate} />
      )}
    </div>
  );
}

function ListView({ items, navigate }) {
  return (
    <div className="table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Workflow</th>
            <th>Stage</th>
            <th>Assigned To</th>
            <th>Priority</th>
            <th className="hide-mobile">Supervisor</th>
            <th className="hide-mobile">SLA</th>
            <th className="hide-mobile">Created</th>
          </tr>
        </thead>
        <tbody>
          {items.map(item => {
            const isOverdue = item.sla_deadline && new Date(item.sla_deadline) < new Date() && !item.completed_at;
            return (
              <tr key={item.id} onClick={() => navigate(`/workflow/items/${item.id}`)}
                style={{ cursor: 'pointer', backgroundColor: isOverdue ? '#fef2f2' : undefined }}>
                <td>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{item.title}</div>
                </td>
                <td style={{ fontSize: 12, color: '#6b7280' }}>{item.workflow_name}</td>
                <td>
                  <span style={{
                    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                    backgroundColor: (item.stage_color || '#6b7280') + '20',
                    color: item.stage_color || '#6b7280',
                  }}>
                    {item.stage_name}
                  </span>
                </td>
                <td style={{ fontSize: 12, fontWeight: 500 }}>
                  {item.owner_names?.length > 0 ? item.owner_names.join(', ') : <span style={{ color: '#d1d5db' }}>—</span>}
                </td>
                <td>
                  <span style={{ fontSize: 11, fontWeight: 600, color: PRIORITY_COLORS[item.priority] }}>
                    {item.priority?.toUpperCase()}
                  </span>
                </td>
                <td className="hide-mobile" style={{ fontSize: 12, color: '#8b5cf6' }}>
                  {item.supervisor_names?.length > 0 ? item.supervisor_names.join(', ') : <span style={{ color: '#d1d5db' }}>—</span>}
                </td>
                <td className="hide-mobile" style={{ fontSize: 12, color: isOverdue ? '#ef4444' : '#6b7280', fontWeight: isOverdue ? 600 : 400 }}>
                  {item.sla_deadline ? formatDate(item.sla_deadline) : '—'}
                </td>
                <td className="hide-mobile" style={{ fontSize: 12, color: '#9ca3af' }}>
                  {formatDate(item.created_at)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function KanbanView({ items, stages, navigate }) {
  const nonTerminalStages = stages.filter(s => s.stage_type !== 'terminal');
  const terminalStages = stages.filter(s => s.stage_type === 'terminal');

  const getStageItems = (stageId) => items.filter(i => i.current_stage_id === stageId);

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 12 }}>
        {nonTerminalStages.map(stage => (
          <div key={stage.id} style={{
            minWidth: 280, maxWidth: 320, flex: '0 0 280px',
            backgroundColor: '#f9fafb', borderRadius: 10, padding: 12,
            border: `2px solid ${stage.color}30`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: stage.color }} />
                <span style={{ fontWeight: 700, fontSize: 13 }}>{stage.name}</span>
              </div>
              <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>
                {getStageItems(stage.id).length}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {getStageItems(stage.id).map(item => (
                <KanbanCard key={item.id} item={item} onClick={() => navigate(`/workflow/items/${item.id}`)} />
              ))}
              {getStageItems(stage.id).length === 0 && (
                <div style={{ padding: 20, textAlign: 'center', color: '#d1d5db', fontSize: 12 }}>Empty</div>
              )}
            </div>
          </div>
        ))}
      </div>
      {terminalStages.length > 0 && (
        <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
          {terminalStages.map(stage => (
            <div key={stage.id} style={{ fontSize: 12, color: '#9ca3af' }}>
              <span style={{ color: stage.color, fontWeight: 600 }}>{stage.name}:</span>{' '}
              {getStageItems(stage.id).length} items
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KanbanCard({ item, onClick }) {
  const isOverdue = item.sla_deadline && new Date(item.sla_deadline) < new Date() && !item.completed_at;
  return (
    <div onClick={onClick} style={{
      padding: '10px 12px', backgroundColor: '#fff', borderRadius: 8,
      border: isOverdue ? '1px solid #fecaca' : '1px solid #e5e7eb',
      cursor: 'pointer', transition: 'box-shadow 0.15s',
    }} className="hover-lift">
      <div style={{ fontWeight: 600, fontSize: 13, color: '#111827', marginBottom: 4, lineHeight: 1.3 }}>
        {item.title}
      </div>
      {item.owner_names?.length > 0 && (
        <div style={{ fontSize: 11, color: '#374151', marginBottom: 2 }}>
          <span style={{ fontWeight: 600 }}>Assigned:</span> {item.owner_names.join(', ')}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: '#9ca3af' }}>by {item.submitter_name}</span>
        <span style={{ fontSize: 10, fontWeight: 700, color: PRIORITY_COLORS[item.priority] }}>
          {item.priority !== 'normal' && item.priority?.toUpperCase()}
        </span>
      </div>
      {isOverdue && (
        <div style={{ fontSize: 10, color: '#ef4444', fontWeight: 600, marginTop: 4 }}>⚠ Overdue</div>
      )}
    </div>
  );
}
